import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * What the analysis found about the vendor's place in search.
 *
 * Read-only: the server returns snapshots already bought and never fetches on
 * a page load. Opening a tab does not spend anything — only an analysis run
 * the vendor asked for does. See [[use-vendor-analysis-api.hook]].
 */

/*
 * Every field added after the first release is optional: snapshots bought
 * before it are served as they were stored, so a page must read an old row
 * without the new numbers and say less rather than show zeroes.
 */

/** Where the rankings sit, in the bands a results page is read by. */
export interface MarketPositions {
    p1?: number;
    p2_3?: number;
    p4_10?: number;
    /** Page two: the cheapest rankings to move onto page one. */
    p11_20?: number;
    p21_50?: number;
    p51_100?: number;
}

/** Since DataForSEO's previous look at the site. */
export interface MarketMovement {
    up?: number;
    down?: number;
    new?: number;
    lost?: number;
}

export interface MarketOverview {
    organicKeywords?: number;
    estimatedMonthlyVisits?: number;
    top3?: number;
    top10?: number;
    top100?: number;
    newKeywords?: number;
    lostKeywords?: number;
    positions?: MarketPositions;
    movement?: MarketMovement;
    /** What the organic traffic would cost bought as ads, a month (USD). */
    trafficValue?: number;
    paid?: { keywords?: number; estimatedMonthlyVisits?: number; cost?: number };
}

export type MarketIntent = 'informational' | 'navigational' | 'commercial' | 'transactional';
export type MarketKeywordMovement = 'new' | 'up' | 'down' | 'same';

export interface MarketKeyword {
    keyword?: string;
    searchVolume?: number | null;
    position?: number | null;
    url?: string | null;
    estimatedVisits?: number;
    previousPosition?: number | null;
    movement?: MarketKeywordMovement | null;
    fullUrl?: string | null;
    title?: string | null;
    /** Cost per click in ads (USD). */
    cpc?: number | null;
    competition?: string | null;
    /** 0–100: how hard the first page is to get onto. */
    difficulty?: number | null;
    intent?: MarketIntent | null;
    /** Twelve monthly search volumes, oldest first. */
    trend?: number[];
    /** Change in searches over a year, in percent. */
    yearlyChange?: number | null;
    trafficValue?: number;
    featuredSnippet?: boolean;
    serpFeatures?: string[];
}

export interface MarketCompetitor {
    domain?: string;
    sharedKeywords?: number;
    avgPosition?: number | null;
    estimatedMonthlyVisits?: number;
    organicKeywords?: number;
    top3?: number;
    top10?: number;
    trafficValue?: number;
    /** Their visits from the searches the two of you share. */
    sharedVisits?: number;
    /** How many of the shared searches they have on page one. */
    sharedTop10?: number;
}

/** A count by category, largest first; '' arrives as 'unknown'. */
export interface MarketCount {
    key: string;
    count: number;
}

export interface MarketBacklinks {
    backlinks?: number;
    referringDomains?: number;
    referringMainDomains?: number;
    brokenBacklinks?: number;
    /** DataForSEO's domain rank, on its default 0–1000 scale. */
    rank?: number | null;
    firstSeen?: string | null;
    nofollowDomains?: number;
    referringIps?: number;
    referringSubnets?: number;
    /** 0–100: how much of the profile looks like spam. */
    spamScore?: number | null;
    brokenPages?: number;
    crawledPages?: number;
    externalLinks?: number;
    internalLinks?: number;
    linkTypes?: MarketCount[];
    attributes?: MarketCount[];
    platforms?: MarketCount[];
    countries?: MarketCount[];
    tlds?: MarketCount[];
}

export interface MarketSeriesPoint {
    month: string;
    summary: Record<string, any>;
}

export interface MarketBusiness {
    title?: string | null;
    rating?: number | null;
    reviews?: number;
    /** False means anyone can edit the listing and nobody is answering reviews. */
    isClaimed?: boolean | null;
    category?: string | null;
    phone?: string | null;
    address?: string | null;
    website?: string | null;
    photos?: number;
    workTime?: boolean;
    mapsUrl?: string | null;
    /** Stars by count — an average hides whether it is consensus or a split. */
    ratingBreakdown?: Record<string, number> | null;
    /** One per branch, in Google's order. The first is the headline listing. */
    locations?: MarketBusinessLocation[];
    totalReviews?: number;
    unclaimed?: number;
    /**
     * Google was searched for this name and returned no listing at all. Not
     * the same as never having looked, and the page says so differently.
     */
    notFound?: boolean;
    searchedFor?: string;
}

export interface MarketBusinessLocation {
    title?: string | null;
    address?: string | null;
    city?: string | null;
    rating?: number | null;
    reviews?: number;
    isClaimed?: boolean | null;
    photos?: number;
    url?: string | null;
    mapsUrl?: string | null;
}

export interface MarketPageAudit {
    url?: string | null;
    score?: number | null;
    title?: string | null;
    description?: string | null;
    /** Only the checks that failed; the passing ones are not a finding. */
    failed?: string[];
    /** The virtues the page has (HTTPS, a doctype…) — from checksVersion 2. */
    passed?: string[];
    /** 2: `failed` holds only real failures; before it, virtues were listed there too. */
    checksVersion?: number;
    timing?: {
        interactive?: number | null;
        domComplete?: number | null;
        largestPaint?: number | null;
        layoutShift?: number | null;
    };
    errors?: number;
}

export interface MarketTechnologies {
    domain?: string | null;
    domainRank?: number | null;
    technologies?: string[];
    socialLinks?: string[];
    lastSeen?: string | null;
}

export interface MarketGapKeyword {
    keyword?: string | null;
    searchVolume?: number;
    theirPosition?: number | null;
    theirUrl?: string | null;
    theirVisits?: number;
    cpc?: number | null;
    difficulty?: number | null;
    intent?: MarketIntent | null;
    trend?: number[];
}

export interface MarketKeywordGap {
    rival: string;
    month: string;
    summary: { total?: number; keywords?: MarketGapKeyword[] };
}

export interface MarketData {
    target: string | null;
    /** False when no website is on file, or nothing has been analysed yet. */
    analysed: boolean;
    month: string | null;
    /**
     * The market these figures were measured in, as an ISO-2 country.
     *
     * Taken from the request that bought them. The same domain scores
     * differently in every country, so a number without its market is not
     * really a number.
     */
    country?: string | null;
    overview: MarketOverview | null;
    keywords: { total?: number; keywords?: MarketKeyword[] } | null;
    competitors: { competitors?: MarketCompetitor[]; platformsExcluded?: string[] } | null;
    backlinks: MarketBacklinks | null;
    screenshot?: { imageUrl?: string } | null;
    /** The Google Business Profile — null until an analysis has read it. */
    business?: MarketBusiness | null;
    pageAudit?: MarketPageAudit | null;
    technologies?: MarketTechnologies | null;
    /** One entry per competitor a gap was bought for. */
    keywordGaps?: MarketKeywordGap[];
    history: { overview?: MarketSeriesPoint[]; backlinks?: MarketSeriesPoint[] };
    /**
     * Rows the plan cut from the lists above (the API never sends them): 0 or
     * absent when nothing is hidden. Aggregates and history stay complete.
     */
    locked?: { keywords?: number; competitors?: number; gaps?: number };
    /** The plan the read was cut for; `limited` when any list was cut. */
    plan?: { slug?: string; limited?: boolean };
}

const BASE = '/services/pms/vendors/me/market';

export const useMarketApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const get = async (): Promise<MarketData> =>
        (await sendAuthGuardedRequest(ApiMethod.GET, BASE)) as MarketData;

    return { get };
};
